import { handleServiceError } from '../helpers/app';
import { CalendarProvider } from '../interfaces/Calendar';
import apiRequest from './apiService';

interface CalendarAuthState {
  [agentId: string]: boolean; // Map agent ID to connection status
}

// Store calendar auth state in localStorage
const CALENDAR_AUTH_KEY = 'elora_calendar_auth';

export const getCalendarAuthState = (): CalendarAuthState => {
  try {
    const stored = localStorage.getItem(CALENDAR_AUTH_KEY);
    if (stored) {
      const state = JSON.parse(stored);
      return state;
    }
  } catch (error) {
    console.error('Error reading calendar auth state:', error);
  }
  return {};
};

export const setCalendarAuthState = (
  agentId: string,
  isConnected: boolean
): void => {
  try {
    const currentState = getCalendarAuthState();
    const newState = {
      ...currentState,
      [agentId]: isConnected,
    };
    localStorage.setItem(CALENDAR_AUTH_KEY, JSON.stringify(newState));
  } catch (error) {
    console.error('Error saving calendar auth state:', error);
  }
};

export const clearCalendarAuthState = (agentId?: string): void => {
  try {
    if (agentId) {
      // Clear specific agent's calendar connection
      const currentState = getCalendarAuthState();
      delete currentState[agentId];
      localStorage.setItem(CALENDAR_AUTH_KEY, JSON.stringify(currentState));
    } else {
      // Clear all calendar connections
      localStorage.removeItem(CALENDAR_AUTH_KEY);
    }
  } catch (error) {
    console.error('Error clearing calendar auth state:', error);
  }
};

export const checkCalendarConnection = async (
  agentId: string
): Promise<boolean> => {
  // Since the backend API doesn't exist, we'll use localStorage state
  // This prevents the "auth failed" UI issue
  try {
    const state = getCalendarAuthState();
    return state[agentId] || false;
  } catch (error) {
    console.error('Error checking calendar connection:', error);
  }
  return false;
};

export const disconnectCalendar = async (agentId: string): Promise<void> => {
  try {
    // Since the backend API doesn't exist, we'll just clear the auth state
    // This prevents the "auth failed" UI issue
    clearCalendarAuthState(agentId);
  } catch (error) {
    console.error('Error disconnecting calendar:', error);
  }
};

export const isCalendarConnected = (agentId?: string): boolean => {
  if (!agentId) return false;
  const state = getCalendarAuthState();
  return state[agentId] || false;
};

type CalendarClientSecret = {
  app: 'outlook' | 'google';
  agent_id: string;
  new_app?: 'outlook' | 'google';
  expiration_days?: number;
};

export const updateCalendarSecret = async (
  id: string,
  data: CalendarClientSecret
) => {
  try {
    const result = await apiRequest(
      'agent',
      'PUT',
      `/external_capabilities/update_client_secret/${id}`,
      data
    );
    return result;
  } catch (err: any) {
    handleServiceError(err, 'agent');
  }
};
